import axiosNest from '../../api/axiosNest';
import type { CsvColumn } from '../../shared/csvExport';

/**
 * Pomiar użycia — pełny ślad działań (decyzja użytkownika 23.10.2026:
 * „przyciski też musimy wiedzieć i generalnie wszystko, co się tam dzieje”).
 * Kształty jak w API (`usage-events.service.ts`): działania, kliknięcia,
 * pozostałe interakcje, oś czasu konta i eksport CSV wszystkich zdarzeń.
 * Wydzielone z `usageApi.ts`, żeby to samo przeszło na linię produkcyjną.
 */

export interface UsageActionStat {
  method: string;
  apiPattern: string;
  count: number;
  users: number;
  statusCodes: { statusCode: number | null; count: number }[];
  errors: number;
  medianDurationMs: number | null;
  route: string | null;
}

export interface UsageClickStat {
  kind: string;
  elementId: string | null;
  label: string | null;
  elementRole: string | null;
  section: string | null;
  route: string | null;
  count: number;
  users: number;
  contract: boolean;
}

export interface UsageInteractionStat {
  kind: string;
  label: string | null;
  count: number;
  users: number;
  medianDurationSec: number | null;
}

export interface UsageTimelineEvent {
  id: number;
  occurredAt: string;
  kind: string;
  route: string | null;
  elementId: string | null;
  elementRole: string | null;
  label: string | null;
  section: string | null;
  statusCode: number | null;
  method: string | null;
  apiPattern: string | null;
  durationMs: number | null;
  recordType: string | null;
  recordId: number | null;
}

export interface UsageTimeline {
  period: { from: string; to: string };
  user: { id: number; username: string | null; role: string | null };
  events: UsageTimelineEvent[];
  truncated: boolean;
  limit: number;
}

export interface TraceQuery {
  from: string;
  to: string;
  department?: string;
  role?: string;
  userId?: string;
  environment?: string;
  /** „Zaloguj jako” (02.11.2026): `only` — tylko zdarzenia z czasu przejęcia. */
  impersonation?: string;
}

const paramsOf = (q: TraceQuery): Record<string, string> => {
  const params: Record<string, string> = { from: q.from, to: q.to };
  for (const k of ['department', 'role', 'userId', 'environment', 'impersonation'] as const) {
    if (q[k]) params[k] = q[k] as string;
  }
  return params;
};

/** Oś czasu jednego konta (`GET /usage-events/timeline`, tylko ADMIN). */
export async function fetchUsageTimeline(q: TraceQuery & { userId: string }): Promise<UsageTimeline> {
  const res = await axiosNest.get<UsageTimeline>('/usage-events/timeline', { params: paramsOf(q) });
  return res.data;
}

/**
 * Eksport CSV wszystkich zdarzeń z filtrami ekranu (`GET /usage-events/export`,
 * tylko ADMIN) — plik buduje API porcjami; tu tylko zapis na dysk.
 */
export async function downloadUsageExport(q: TraceQuery): Promise<void> {
  const res = await axiosNest.get<Blob>('/usage-events/export', { params: paramsOf(q), responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = `pomiar-zdarzenia_${q.from}_${q.to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Teksty dla operatora ──────────────────────────────────────────────

export const TRACE_NOTE =
  'Od 23.10.2026 zapisujemy każde kliknięcie w przycisk, odnośnik, zakładkę, pozycję menu, pole wyboru i wiersz listy (ze stałym napisem z ekranu), każde działanie zmieniające dane (rodzaj zapytania, kod odpowiedzi i czas), okna, filtry (sama nazwa), eksport, wylogowanie i czas na ekranie. Treści pól, wartości filtrów, treści komórek ani nazw kontrahentów i kierowców nie zapisujemy.';
export const EXPORT_ALL_BUTTON = 'Eksport wszystkich zdarzeń (CSV)';
export const EXPORT_ALL_ERROR = 'Nie udało się pobrać eksportu. Spróbuj krótszego okresu.';
export const TIMELINE_PICK_USER = 'Wybierz konto w filtrze „Konto”, żeby zobaczyć jego oś czasu.';
export const TIMELINE_ERROR = 'Nie udało się wczytać osi czasu konta.';

/** Rodzaj zdarzenia po polsku. */
export const KIND_LABELS: Readonly<Record<string, string>> = {
  screen: 'Wejście na ekran',
  screenTime: 'Czas na ekranie',
  click: 'Kliknięcie',
  dialogConfirm: 'Zatwierdzenie okna',
  dialogOpen: 'Otwarcie okna',
  dialogClose: 'Zamknięcie okna',
  action: 'Działanie',
  apiError: 'Błąd odczytu',
  filter: 'Zmiana filtru',
  sort: 'Zmiana sortowania',
  pagination: 'Zmiana strony listy',
  export: 'Eksport',
  logout: 'Wylogowanie',
};
export const kindLabel = (k: string | null | undefined) => (k ? (KIND_LABELS[k] ?? k) : '—');

/** Rola elementu po polsku. */
export const ELEMENT_ROLE_LABELS: Readonly<Record<string, string>> = {
  button: 'przycisk',
  link: 'odnośnik',
  tab: 'zakładka',
  menuitem: 'pozycja menu',
  checkbox: 'pole wyboru',
  switch: 'przełącznik',
  radio: 'opcja',
  row: 'wiersz listy',
  chip: 'znacznik',
  option: 'opcja listy',
  combobox: 'lista rozwijana',
  download: 'pobranie pliku',
  other: 'inny element',
};
export const elementRoleLabel = (r: string | null | undefined) => (r ? (ELEMENT_ROLE_LABELS[r] ?? r) : '—');

/** Metoda działania po polsku (z metodą w nawiasie). */
const METHOD_LABELS: Readonly<Record<string, string>> = {
  POST: 'utworzenie / czynność',
  PUT: 'zapis',
  PATCH: 'zmiana',
  DELETE: 'usunięcie',
  GET: 'odczyt',
};
export const methodLabel = (m: string | null | undefined) => (m ? `${METHOD_LABELS[m] ?? m} (${m})` : '—');

/** Kody odpowiedzi: „201 × 5, 409 × 1, bez odpowiedzi × 1”. */
export function statusCodesText(codes: { statusCode: number | null; count: number }[]): string {
  if (codes.length === 0) return '—';
  return codes.map((c) => `${c.statusCode ?? 'bez odpowiedzi'} × ${c.count}`).join(', ');
}

/** Co kliknięto: etykieta, identyfikator albo rola. */
export const clickName = (c: { label: string | null; elementId: string | null; elementRole: string | null }) =>
  c.label ?? c.elementId ?? elementRoleLabel(c.elementRole);

/** Czas w ms jako „120 ms” / „2,4 s”. */
export function msText(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toLocaleString('pl-PL', { maximumFractionDigits: 1 })} s`;
}

/** Chwila jako „16.11.2026 09:00:05” czasu polskiego. */
export function momentText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Opis zdarzenia osi czasu jednym zdaniem — bez treści (jej nie ma w danych). */
export function timelineWhat(e: UsageTimelineEvent): string {
  switch (e.kind) {
    case 'action':
      return `${methodLabel(e.method)} ${e.apiPattern ?? ''} → ${e.statusCode ?? 'bez odpowiedzi'} (${msText(e.durationMs)})`;
    case 'apiError':
      return `${e.method ?? ''} ${e.apiPattern ?? ''} → ${e.statusCode ?? ''}`;
    case 'screenTime':
      return msText(e.durationMs);
    case 'dialogClose':
      return `${e.label ?? 'okno'} (otwarte ${msText(e.durationMs)})`;
    case 'filter':
    case 'sort':
    case 'pagination':
      return e.label ?? '—';
    case 'screen':
    case 'logout':
      return '';
    default: {
      // Wiersz listy ma etykietę „wiersz listy X” — bez powtarzania rodzaju.
      if (e.elementRole === 'row') return e.label ?? 'wiersz listy';
      const name = e.label ?? e.elementId ?? '';
      const role = e.elementRole ? elementRoleLabel(e.elementRole) : '';
      return [role, name && `„${name}”`, e.section && `w: ${e.section}`].filter(Boolean).join(' ');
    }
  }
}

// ── Eksport CSV widoków ───────────────────────────────────────────────

type Rec = Record<string, unknown>;

export const ACTION_CSV = (routeLabel: (r: string | null) => string): readonly CsvColumn[] => [
  { header: 'Metoda', value: (r) => (r as Rec).method },
  { header: 'Zapytanie (wzorzec)', value: (r) => (r as Rec).apiPattern },
  { header: 'Liczba', value: (r) => (r as Rec).count },
  { header: 'Użytkownicy', value: (r) => (r as Rec).users },
  { header: 'Odmowy i błędy', value: (r) => (r as Rec).errors },
  { header: 'Kody odpowiedzi', value: (r) => statusCodesText((r as unknown as UsageActionStat).statusCodes) },
  { header: 'Mediana czasu (ms)', value: (r) => (r as Rec).medianDurationMs },
  { header: 'Najczęściej z ekranu', value: (r) => routeLabel((r as Rec).route as string | null) },
];

export const CLICK_CSV = (routeLabel: (r: string | null) => string): readonly CsvColumn[] => [
  { header: 'Rodzaj', value: (r) => kindLabel((r as Rec).kind as string) },
  { header: 'Etykieta', value: (r) => (r as Rec).label },
  { header: 'Identyfikator', value: (r) => (r as Rec).elementId },
  { header: 'Rola elementu', value: (r) => elementRoleLabel((r as Rec).elementRole as string | null) },
  { header: 'Sekcja', value: (r) => (r as Rec).section },
  { header: 'Najczęściej na ekranie', value: (r) => routeLabel((r as Rec).route as string | null) },
  { header: 'Kliknięcia', value: (r) => (r as Rec).count },
  { header: 'Użytkownicy', value: (r) => (r as Rec).users },
];

export const INTERACTION_CSV: readonly CsvColumn[] = [
  { header: 'Rodzaj', value: (r) => kindLabel((r as Rec).kind as string) },
  { header: 'Nazwa', value: (r) => (r as Rec).label },
  { header: 'Liczba', value: (r) => (r as Rec).count },
  { header: 'Użytkownicy', value: (r) => (r as Rec).users },
  { header: 'Mediana czasu (s)', value: (r) => (r as Rec).medianDurationSec },
];

export const TIMELINE_CSV = (routeLabel: (r: string | null) => string): readonly CsvColumn[] => [
  { header: 'Czas polski', value: (r) => momentText((r as Rec).occurredAt as string) },
  { header: 'Rodzaj', value: (r) => kindLabel((r as Rec).kind as string) },
  { header: 'Ekran', value: (r) => routeLabel((r as Rec).route as string | null) },
  { header: 'Co', value: (r) => timelineWhat(r as unknown as UsageTimelineEvent) },
  { header: 'Rekord', value: (r) => ((r as Rec).recordId ? `${(r as Rec).recordType} ${(r as Rec).recordId}` : '') },
];
